// Position de l'agent : relevé ponctuel (début de mission, constat) et suivi pendant l'affichage
// d'une carte, si l'appareil l'autorise. Aucun suivi continu en arrière-plan (D-121).
import type { PositionTerrain } from './appareil';

export type AutorisationPosition = 'inconnue' | 'accordee' | 'refusee' | 'indisponible';

export interface EtatPosition {
  position: PositionTerrain | null;
  autorisation: AutorisationPosition;
  erreur: string | null;
}

function versPosition(p: GeolocationPosition): PositionTerrain {
  return { longitude: p.coords.longitude, latitude: p.coords.latitude, precision_m: Number.isFinite(p.coords.accuracy) ? Math.round(p.coords.accuracy) : null, horodatage: new Date(p.timestamp).toISOString() };
}

function messageErreurPosition(e: GeolocationPositionError): { autorisation: AutorisationPosition; erreur: string } {
  if (e.code === e.PERMISSION_DENIED) return { autorisation: 'refusee', erreur: "La position n'est pas autorisée sur cet appareil." };
  if (e.code === e.TIMEOUT) return { autorisation: 'accordee', erreur: 'Position non obtenue dans le délai.' };
  return { autorisation: 'indisponible', erreur: 'Position indisponible pour le moment.' };
}

export function positionDisponible(): boolean {
  return typeof navigator !== 'undefined' && 'geolocation' in navigator;
}

/** Relevé ponctuel ; résout null (avec le motif) plutôt que de bloquer l'action (R18). */
export function releverPosition(delaiMs = 10_000): Promise<EtatPosition> {
  if (!positionDisponible()) return Promise.resolve({ position: null, autorisation: 'indisponible', erreur: "Cet appareil n'expose pas de position." });
  return new Promise((resoudre) => {
    navigator.geolocation.getCurrentPosition(
      (p) => resoudre({ position: versPosition(p), autorisation: 'accordee', erreur: null }),
      (e) => resoudre({ position: null, ...messageErreurPosition(e) }),
      { enableHighAccuracy: true, timeout: delaiMs, maximumAge: 30_000 },
    );
  });
}

/** Suivi pendant l'affichage d'une carte ; renvoie la fonction d'arrêt. */
export function suivrePosition(rappel: (e: EtatPosition) => void): () => void {
  if (!positionDisponible()) {
    rappel({ position: null, autorisation: 'indisponible', erreur: "Cet appareil n'expose pas de position." });
    return () => undefined;
  }
  const id = navigator.geolocation.watchPosition(
    (p) => rappel({ position: versPosition(p), autorisation: 'accordee', erreur: null }),
    (e) => rappel({ position: null, ...messageErreurPosition(e) }),
    { enableHighAccuracy: true, maximumAge: 15_000, timeout: 20_000 },
  );
  return () => navigator.geolocation.clearWatch(id);
}

/** Distance en mètres entre deux positions (formule de haversine). */
export function distanceMetres(a: { longitude: number; latitude: number }, b: { longitude: number; latitude: number }): number {
  const r = 6_371_000;
  const rad = (d: number) => (d * Math.PI) / 180;
  const dLat = rad(b.latitude - a.latitude);
  const dLon = rad(b.longitude - a.longitude);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.latitude)) * Math.cos(rad(b.latitude)) * Math.sin(dLon / 2) ** 2;
  return 2 * r * Math.asin(Math.sqrt(h));
}

export function distanceLisible(m: number): string {
  return m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(1).replace('.', ',')} km`;
}
